import { useSyncExternalStore } from 'react';

/**
 * Paige Scan for Windows: the one place the download lives, and the words for getting a computer
 * connected, said the same under Company, Scanners and devices and in the workbench's sheets
 * (docs/engineering/folder-monitor.md, section 7; docs/engineering/signing.md).
 *
 * The link and its version are read at run time from `GET /v1/scan/download`, which serves what the
 * publish script recorded after verifying the signed release `latest` holds, so the buttons never drift
 * behind a release. Until that answers (or when it cannot), the build's own signed fallback below stands;
 * the release gate checks it (`tools/release/verify-downloads.ps1`). `VITE_PAIGE_SCAN_DOWNLOAD` still
 * overrides the fallback for a build that needs to.
 */
const DEFAULT_DOWNLOAD =
  'https://storage.googleapis.com/paige-v2-downloads/scan/PaigeScan-1.5.22.msi';

function configured(): string {
  const value: unknown = import.meta.env?.['VITE_PAIGE_SCAN_DOWNLOAD'];
  return typeof value === 'string' && /^https:\/\//.test(value) ? value : DEFAULT_DOWNLOAD;
}

/** The build's fallback link (before the API answers); every button reads `useScanDownload` instead. */
export const PAIGE_SCAN_DOWNLOAD = configured();

/**
 * The release a link is, read from its file name (`PaigeScan-1.5.2.msi`); null for
 * `PaigeScan-latest.msi`, which names no version.
 */
export function versionOf(url: string): string | null {
  return /PaigeScan-(\d+\.\d+\.\d+)\.msi$/.exec(url)?.[1] ?? null;
}

export const PAIGE_SCAN_VERSION = versionOf(PAIGE_SCAN_DOWNLOAD);

/** Paige Scan's download: the signed MSI's link and its version (null when the link names none). */
export interface ScanDownload {
  url: string;
  version: string | null;
}

const VERSIONED =
  /^https:\/\/storage\.googleapis\.com\/paige-v2-downloads\/scan\/PaigeScan-\d+\.\d+\.\d+\.msi$/;

let current: ScanDownload = { url: PAIGE_SCAN_DOWNLOAD, version: PAIGE_SCAN_VERSION };
let asked = false;
const listeners = new Set<() => void>();

/** Asks the API once per page; a link outside the bucket's versioned MSIs is never taken. */
function askOnce(): void {
  if (asked || typeof fetch !== 'function') return;
  asked = true;
  void fetch('/v1/scan/download', { credentials: 'omit', cache: 'no-store' })
    .then((r) => (r.ok ? (r.json() as Promise<{ url?: unknown; version?: unknown }>) : null))
    .then((body) => {
      if (!body || typeof body.url !== 'string' || !VERSIONED.test(body.url)) return;
      current = { url: body.url, version: versionOf(body.url) };
      for (const l of listeners) l();
    })
    .catch(() => undefined);
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  askOnce();
  return () => listeners.delete(listener);
}

/** The current download, read from Paige once per page and shared by every button. */
export function useScanDownload(): ScanDownload {
  return useSyncExternalStore(
    subscribe,
    () => current,
    () => current,
  );
}

/** For tests: forget what was read, so the next button asks again. */
export function resetScanDownload(next?: ScanDownload): void {
  current = next ?? { url: PAIGE_SCAN_DOWNLOAD, version: PAIGE_SCAN_VERSION };
  asked = next !== undefined;
}

/** The download's own words. */
export const PAIGE_SCAN_DOWNLOAD_LABEL = 'Download Paige Scan for Windows';

/**
 * Connecting a computer, after the download: three steps, an admin's to take. The last one ends where
 * the person is: Company, Devices lists the computer; a job's sheet adds that job's folder.
 */
export function paigeScanSteps(where: 'devices' | 'job'): readonly [string, string, string] {
  return [
    'Install it on the computer that can see the scanner’s folder (administrator).',
    'Paige Scan opens by itself after the install: choose Sign in to Paige.',
    where === 'devices'
      ? 'Allow this computer as an admin of this company: it shows here; then add its folders in Paige Scan.'
      : 'Allow this computer as an admin of this company; then add this job’s folder in Paige Scan.',
  ];
}

/** What someone who is not an admin is told where an admin would get the download. */
export const ASK_ADMIN_FOR_FOLDER =
  'Ask your company admin to set up a watched folder for this job.';
